import { useMemo, useState } from "react";

import Button from "components/Button";
import HollowButton from "components/HollowButton";

import dispatchChartService from "api/dispatchChartService";
import mailroomService from "api/mailroomService";
import { IDispatchListItem } from "dto/Mailroom/IDispatchChart";
import { ENotificationType, notification } from "utils/notification";

import styles from "./outgoing.module.scss";

interface Props {
  parcels: IDispatchListItem[];
  onClose: () => void;
  onDone: () => void;
}

// „Przesyłka wspólna" — port OutGroupingParcelWizard z Magistratu: kilka pism do jednego
// adresata scala się w jedną kopertę (przesyłkę grupującą) z własną sygnaturą, która
// podlega wysyłce (karta nadawcza / nadanie / zwrotka). Użytkownik wybiera formę doręczenia
// koperty i adresata spośród adresatów grupowanych pism (zwykle jednego — grupujemy do tej
// samej osoby; przy różnych adresatach lista pokazuje wszystkich do wyboru).
const GroupParcelsModal = ({ parcels, onClose, onDone }: Props) => {
  const { mutate, isLoading } = dispatchChartService.useGroupParcels();
  const { data: kindsData, isLoading: loadingKinds } =
    mailroomService.useGetDeliveryKinds();

  // Formy papierowe (koperta = fizyczna wysyłka), bez zaślepki „Inne".
  const kinds = useMemo(
    () =>
      (kindsData?.data ?? [])
        .filter((k) => k.Name !== "Inne" && !(k.IsElectronic ?? false))
        .slice()
        .sort((a, b) => a.SortOrder - b.SortOrder),
    [kindsData]
  );

  // Adresaci bez duplikatów (nazwa + adres) — jak GroupBy(EntityId, AddressId) w Magistracie.
  const receivers = useMemo(() => {
    const seen = new Map<string, IDispatchListItem>();
    for (const p of parcels) {
      const key = `${p.ReceiverName ?? ""}|${p.ReceiverAddress ?? ""}`;
      if (!seen.has(key)) seen.set(key, p);
    }
    return Array.from(seen.values());
  }, [parcels]);

  const [kindId, setKindId] = useState<number | null>(
    parcels[0]?.DeliveryKindId ?? null
  );
  const [receiverParcelId, setReceiverParcelId] = useState<string>(
    receivers[0]?.Id ?? ""
  );

  const submit = () => {
    mutate(
      {
        ParcelIds: parcels.map((p) => p.Id),
        DeliveryKindId: kindId,
        ReceiverParcelId: receiverParcelId || null,
      },
      {
        onSuccess: (res) => {
          notification({
            type: ENotificationType.SUCCESS,
            title: "Utworzono kopertę wspólną",
            text: `Sygnatura ${res.data.ParcelNumber ?? ""} — ${
              res.data.GroupedCount
            } pism w jednej przesyłce.`,
          });
          onDone();
        },
        onError: (e) =>
          notification({
            type: ENotificationType.ERROR,
            title: "Nie udało się zgrupować",
            text:
              (e as { response?: { data?: { Message?: string } } })?.response
                ?.data?.Message ?? "Spróbuj ponownie po odświeżeniu listy.",
          }),
      }
    );
  };

  return (
    <div className={styles.overlay} onMouseDown={onClose}>
      <div className={styles.modal} onMouseDown={(e) => e.stopPropagation()}>
        <div className={styles.modalTitle}>
          Przesyłka wspólna — {parcels.length} pism w jednej kopercie
        </div>
        <div className={styles.modalBody}>
          <div className={styles.parcelPreview}>
            {parcels.map((p) => (
              <div key={p.Id} className={styles.previewRow}>
                <span className={styles.sig}>{p.ParcelNumber ?? "—"}</span>
                <span>{p.Title ?? p.ReceiverName ?? "—"}</span>
                <span className={styles.dim}>{p.DeliveryKindName ?? ""}</span>
              </div>
            ))}
          </div>

          <div className={styles.field}>
            <label>Forma doręczenia koperty</label>
            <select
              value={kindId ?? ""}
              onChange={(e) =>
                setKindId(e.target.value === "" ? null : Number(e.target.value))
              }
              disabled={loadingKinds}
            >
              {loadingKinds && <option value="">Ładowanie…</option>}
              {kinds.map((k) => (
                <option key={k.Id} value={k.Id}>
                  {k.Name}
                </option>
              ))}
            </select>
          </div>

          <div className={styles.field}>
            <label>Adresat koperty</label>
            {receivers.length > 1 && (
              <div className={styles.warn}>
                Grupowane pisma mają różnych adresatów — koperta trafi do
                jednego z nich.
              </div>
            )}
            <div className={styles.parcelPreview}>
              {receivers.map((r) => (
                <label key={r.Id} className={styles.check}>
                  <input
                    type="radio"
                    name="grouping-receiver"
                    checked={receiverParcelId === r.Id}
                    onChange={() => setReceiverParcelId(r.Id)}
                  />
                  <span>
                    <strong>{r.ReceiverName ?? "—"}</strong>
                    {r.ReceiverAddress ? (
                      <span className={styles.dim}> · {r.ReceiverAddress}</span>
                    ) : null}
                  </span>
                </label>
              ))}
            </div>
          </div>

          <div className={styles.hint}>
            Koperta otrzyma nową sygnaturę i zastąpi pisma na liście „Do
            wysyłki"; pisma przyjmą formę doręczenia koperty i odziedziczą
            nadanie oraz potwierdzenie odbioru. Rozgrupowanie możliwe do czasu
            nadania.
          </div>
        </div>
        <div className={styles.modalFooter}>
          <HollowButton text="Anuluj" onClick={onClose} />
          <Button
            text="Utwórz"
            onClick={submit}
            loading={isLoading}
            disabled={kindId == null || !receiverParcelId}
          />
        </div>
      </div>
    </div>
  );
};

export default GroupParcelsModal;
